import { Team } from '../../types';
import { rosterImportRegistry } from './registry';

const findStandardRace = (parsedRace: string): string => {
  const knownRaces = [
    "Amazon", "Black Orc", "Bretonnian", "Chaos Chosen", "Chaos Dwarf", "Chaos Renegades",
    "Dark Elf", "Dwarf", "Elven Union", "Gnome", "Goblin", "Halfling", "High Elf",
    "Human", "Imperial Nobility", "Khorne", "Lizardmen", "Necromantic Horror", "Norse",
    "Nurgle", "Ogre", "Old World Alliance", "Orc", "Shambling Undead", "Skaven",
    "Snotling", "Tomb Kings", "Underworld Denizens", "Vampire", "Wood Elf"
  ];
  const clean = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const cleanedInput = clean(parsedRace);
  const matched = knownRaces.find(r => clean(r) === cleanedInput);
  return matched || parsedRace;
};

export const importRoster = async (file: File, strategyId: string): Promise<Team> => {
  const strategy = rosterImportRegistry.getStrategy(strategyId);
  if (!strategy) {
    throw new Error('Selected import platform strategy not found.');
  }

  // 1. Parse using Strategy DTO
  const dto = await strategy.parse(file);
  const standardRace = findStandardRace(dto.race);

  // 2. Load matching template positionals to get standard stats/Primary/Secondary
  let teamData: any;
  try {
    teamData = await import(`../../data/${standardRace.replace(/\s+/g, '')}.json`);
  } catch (err) {
    console.error("Failed to load template JSON, importing raw parsed players", err);
  }
  const positionals = teamData?.default?.Positionals || teamData?.Positionals || [];

  // 3. Map DTO to full Team structure
  const clean = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '').trim();
  const findMatchingPositional = (parsedPos: string) => {
    const cleanedParsed = clean(parsedPos.split(' (')[0]);
    // Try exact match
    let matched = positionals.find((p: any) => clean(p.Position.split(' (')[0]) === cleanedParsed);
    if (matched) return matched;

    // Try substring match
    matched = positionals.find((p: any) => {
      const cleanDb = clean(p.Position.split(' (')[0]);
      return cleanDb.includes(cleanedParsed) || cleanedParsed.includes(cleanDb);
    });
    if (matched) return matched;

    // Try word token overlap match (for cases like "Altern Forest Treeman" vs "Athel Forest Treeman")
    const getWordTokens = (str: string) => {
      return str.split(' (')[0]
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .split(/\s+/)
        .filter(t => t.length > 0);
    };
    const parsedTokens = getWordTokens(parsedPos);
    if (parsedTokens.length > 0) {
      let bestMatch: any = null;
      let maxOverlap = 0;
      for (const p of positionals) {
        const dbTokens = getWordTokens(p.Position);
        const overlap = parsedTokens.filter(t => dbTokens.includes(t)).length;
        if (overlap > maxOverlap) {
          maxOverlap = overlap;
          bestMatch = p;
        }
      }
      // Only accept as a match if there is at least some significant overlap (e.g. at least 1 word)
      if (maxOverlap > 0) {
        return bestMatch;
      }
    }

    return null;
  };

  // Reconstruct players list (exactly 16 slots)
  const mappedPlayers = Array.from({ length: 16 }, (_, i) => ({
    number: i + 1,
    name: '',
    position: ''
  })) as any[];

  dto.players.forEach((p, idx) => {
    const matched = findMatchingPositional(p.position);
    const playerObj: any = {
      number: idx + 1, // Contiguous jersey numbers 1 to N!
      name: p.name || `Player ${idx + 1}`,
      position: matched ? matched.Position : (p.position || 'Custom Player'),
      spp: p.spp,
      level: p.level,
      careerStats: p.careerStats
    };

    if (matched) {
      playerObj.stats = {
        MA: p.stats.MA || matched.MA,
        ST: p.stats.ST || matched.ST,
        AG: p.stats.AG || matched.AG,
        PA: p.stats.PA || matched.PA,
        AR: p.stats.AR || matched.AR,
        Skills: p.skills.join(', '),
        Primary: matched.Primary,
        Secondary: matched.Secondary,
        Cost: p.stats.Cost || matched.Cost
      };
    } else {
      playerObj.isUnmatched = true;
      // Keep all parsed statistics and skills directly from the PDF
      playerObj.stats = {
        MA: p.stats.MA || '6',
        ST: p.stats.ST || '3',
        AG: p.stats.AG || '3+',
        PA: p.stats.PA || '4+',
        AR: p.stats.AR || '8+',
        Skills: p.skills.join(', '),
        Primary: 'G',
        Secondary: 'A S P',
        Cost: p.stats.Cost || '0'
      };
    }

    // Put in slot `idx` contiguously!
    mappedPlayers[idx] = playerObj;
  });

  // Prepare team structure
  const now = new Date().toISOString();
  let tier = 1;
  try {
    const tiersData = await import('../../data/team_tiers.json');
    tier = (tiersData.default as any)?.[standardRace] || (tiersData as any)?.[standardRace] || 1;
  } catch (err) {
    console.error("Failed to load tier metadata", err);
  }

  // Map staff counts to database keys
  const staffMap: Record<string, number> = {};
  if (dto.staffCounts.rerolls !== undefined) staffMap['Re-roll'] = dto.staffCounts.rerolls;
  if (dto.staffCounts.assistantCoaches !== undefined) staffMap['Assistant Coach'] = dto.staffCounts.assistantCoaches;
  if (dto.staffCounts.apothecary !== undefined) staffMap['Apothecary'] = dto.staffCounts.apothecary;

  const inducementMap: Record<string, number> = {};
  if (dto.staffCounts.dedicatedFans !== undefined) inducementMap['Dedicated Fans'] = dto.staffCounts.dedicatedFans;

  // Imported rosters are assumed to be existing, managed teams by default.
  // Coaches can explicitly un-register in the UI if they want to use draft flows.
  const isRegistered = true;

  const newTeam: Team = {
    id: crypto.randomUUID(),
    teamName: dto.teamName,
    region: standardRace,
    coachName: dto.coachName || 'Imported Coach',
    players: mappedPlayers,
    formations: [],
    staffCounts: staffMap,
    inducementCounts: inducementMap,
    selectedStarPlayers: {},
    selectedLeague: undefined,
    rulesetMode: 'league',
    goldBudget: 1000000,
    treasury: dto.treasury,
    isRegistered,
    tier,
    createdAt: now,
    updatedAt: now
  };

  return newTeam;
};
